import { useEffect, useState } from 'react'
import './ChannelPartnerManagementPage.css'
import Navbar from '@/components/Navbar'
import HeaderBar from '@/components/HeaderBar'
import CreatePartnerModal from '@/components/CreatePartnerModal'
import EditPartnerModal from '@/components/EditPartnerModal'
import DeletePartnerModal from '@/components/DeletePartnerModal'
import { useUserRoles } from '@/contexts/UserRolesContext'
import { Helmet } from 'react-helmet-async'
import { PAGES } from '@/constants/pages'
import PermissionGuard from '@/components/PermissionGuard'
import { PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { Partner } from '@/services/PartnerService'
import { usePartner } from '@/contexts/partnerContext'

export default function ChannelPartnerManagementPage() {
  const { loading: rolesLoading } = useUserRoles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null)
  const [deletingPartner, setDeletingPartner] = useState<Partner | null>(null)

  const { setPartners, refreshPartners, partners } = usePartner();

  const handleEditPartner = (partnerId: number) => {
    const partner = partners.find((p) => p.id === partnerId)
    if (partner) {
      setEditingPartner(partner)
    }
  }

  const handleDeletePartner = (partnerId: number) => {
    const partner = partners.find((p) => p.id === partnerId)
    if (partner) {
      setDeletingPartner(partner)
    }
  }

  const handleCreatePartner = () => {
    setShowCreateModal(true)
  }

  const handleModalClose = () => {
    setShowCreateModal(false)
  }

  const handleModalSuccess = async () => {
    try {
      await refreshPartners();
    } catch (err) {
      console.error('Error refreshing channel partners:', err)
    }
  }

  const handleEditModalClose = () => {
    setEditingPartner(null)
  }

  const handleEditModalSuccess = async () => {
    try {
      await refreshPartners();
    } catch (err) {
      console.error('Error refreshing channel partners:', err)
    }
  }

  const handleDeleteModalClose = () => {
    setDeletingPartner(null)
  }

  const handleDeleteModalSuccess = async () => {
    // Refresh the partners list after successful deletion
    try {
      await refreshPartners();
      setError(null)
    } catch (err) {
      console.error('Error refreshing channel partners:', err)
      setError(err instanceof Error ? err.message : 'Failed to refresh channel partners')
    }
  }

  useEffect(() => {
    if (rolesLoading) return

    const fetchPartners = async () => {
      try {
        setLoading(true)
        await refreshPartners();
        setError(null)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load channel partners. Please try again later.'
        setError(errorMessage)
        console.error('Error fetching channel partners:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPartners()
  }, [rolesLoading])

  if (rolesLoading || loading) {
    return (
      <div className="page-container">
        <Navbar />
        <main className="page-main-content">
          <HeaderBar />
          <div className="page-content">
            <div className="loading-container">
              <p>Loading channel partners...</p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="page-container">
      <Helmet>
        <title>{PAGES.PARTNERS.title}</title>
      </Helmet>
      <Navbar />
      <main className="page-main-content">
        <HeaderBar />
        <div className="page-content">
          <h2>Channel Partners</h2>

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span className="alert-message">{error}</span>
              <button
                className="alert-close"
                onClick={() => setError(null)}
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          <PermissionGuard resource={RESOURCES.PARTNERS} scope={PermissionScope.CREATE}>
            <div className="page-actions">
              <button
                className="btn btn-success"
                onClick={handleCreatePartner}
                title="Create new channel partner"
              >
                Create Partner
              </button>
            </div>
          </PermissionGuard>

          <div className="partners-table-container">
            <table className="partners-table">
              <thead>
                <tr>
                  <th>Partner ID</th>
                  <th>Partner Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={partner.id}>
                    <td>{partner.id}</td>
                    <td>{partner.name}</td>
                    <td>
                      <div className="actions">
                        <PermissionGuard resource={RESOURCES.PARTNERS} scope={PermissionScope.UPDATE}>
                          <button className="action-btn edit-btn" onClick={() => handleEditPartner(partner.id)} title="Edit partner">
                            Edit
                          </button>
                        </PermissionGuard>
                        <PermissionGuard resource={RESOURCES.PARTNERS} scope={PermissionScope.DELETE}>
                          <button className="action-btn delete-btn" onClick={() => handleDeletePartner(partner.id)} title="Delete partner">
                            Delete
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {partners.length === 0 && (
              <div className="no-data">
                <p>No channel partners found.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {showCreateModal && (
        <CreatePartnerModal
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          partners={partners}
        />
      )}

      {editingPartner && (
        <EditPartnerModal
          partner={editingPartner}
          onClose={handleEditModalClose}
          onSuccess={handleEditModalSuccess}
        />
      )}

      {deletingPartner && (
        <DeletePartnerModal
          partner={deletingPartner}
          onClose={handleDeleteModalClose}
          onSuccess={handleDeleteModalSuccess}
        />
      )}
    </div>
  )
}
